import { ChangeEvent, forwardRef } from "react";

import { InlineMessage } from "../inline-message/inline-message";
import {
  checkboxClass,
  checkboxContainerClass,
  checkboxLabelClass,
  checkedClass,
  checkmarkBaseClass,
  disabledClass,
  errorCheckedClass,
  errorClass,
} from "./checkbox.css";

interface CheckboxProps {
  /**
   *
   * Content to display for the checkbox
   */
  content?: string | JSX.Element;
  /**
   *
   * Is there an error?
   */
  hasError?: boolean;
  /**
   * Is the checkbox checked?
   */
  isChecked?: boolean;
  /**
   * ID used for accessibility
   */
  id: string;
  /**
   * Is the input disabled?
   */
  isDisabled?: boolean;
  /**
   * Is the input required?
   */
  isRequired?: boolean;
  /**
   * onBlur handler
   */
  onBlur?: () => void;
  /**
   * onChange handler
   */
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  /**
   * onFocus handler
   */
  onFocus?: () => void;
  /**
   * Tab index for the input
   */
  tabIndex?: number;
  /**
   * Test id
   */
  testId?: string;
  /**
   * caption for the input
   */
  captionMessage?: string;
  /**
   * Error message for the input
   */
  errorMessage?: string;
  /**
   * Error Message Key for caption test id
   */
  errorMessageKey?: string;
}

/**
 * Primary Checkbox component. Is controlled and accepts a forwarded Ref!
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      content,
      onFocus,
      onBlur,
      isDisabled = false,
      isRequired = false,
      id,
      hasError = false,
      isChecked,
      onChange,
      tabIndex,
      testId,
      errorMessage,
      errorMessageKey,
      captionMessage,
    }: CheckboxProps,
    ref
  ) => {
    const hasMessage = captionMessage || errorMessage;
    const captionID = `${id}-${errorMessageKey}-caption`;

    // CLASS LOGIC
    const checkedClassStyles = hasError ? errorCheckedClass : checkedClass;
    const checkedClassLogic: string = isChecked ? checkedClassStyles : "";
    const errorClassLogic: string = hasError ? errorClass : "";
    const disabledClassLogic: string = isDisabled ? disabledClass : "";

    return (
      <div className={`${checkboxContainerClass}`}>
        <label
          className={`${checkboxLabelClass} ${disabledClassLogic}`}
          htmlFor={id}
        >
          <input
            aria-required={isRequired}
            className={`${checkboxClass}`}
            checked={isChecked}
            disabled={isDisabled}
            id={id}
            role="checkbox"
            type="checkbox"
            name={id}
            onBlur={onBlur}
            onChange={onChange}
            onFocus={onFocus}
            ref={ref}
            tabIndex={tabIndex}
            data-testid={testId ? `${testId}-input` : `${id}-input`}
          />
          <span
            data-testid={testId ?? id}
            className={`${checkmarkBaseClass} ${checkmarkBaseClass} ${checkedClassLogic} ${errorClassLogic} ${disabledClassLogic} validateCheckbox`}
          ></span>
          {content}
        </label>
        {hasMessage && (
          <InlineMessage
            id={captionID}
            message={errorMessage || captionMessage}
            type={errorMessage ? "error" : "info"}
          />
        )}
      </div>
    );
  }
);

Checkbox.displayName = "Checkbox";
